/**
 * ProductEdit Page - Create or edit a mix design / recipe.
 *
 * Layout mirrors the Figma "Create new Mix Design / Recipe": a single
 * left-aligned column — core fields, the three Default Targets and Limits
 * dropdowns with labels on the left, and the Batching Plants linking table
 * (many-to-many; replaces the old single supplier-plant dropdown).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  FormHelperText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import productsService, {
  type ProductCreate,
  type ProductUpdate,
  type SupplierPlant,
  type MassConcreteDefault,
  type EvaporationDefault,
  type TargetGroup,
} from '../services/products';
import maturityService, {
  type NurseSaulTarget,
  type ArrheniusTarget,
} from '../services/maturity';
import { useAuth } from '../context/AuthContext';
import { useCompanyStrengthUnit } from '../utils/useCompanyStrengthUnit';
import { apiErrorText } from '../utils/apiError';

interface MaturityOption {
  id: string;
  label: string;
}

const formatLinkDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const hasZone = /Z|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(hasZone ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
};

const ProductEdit: React.FC = () => {
  const { t } = useTranslation();
  const { id: productId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !productId || productId === 'new';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Lookup data
  const [supplierPlants, setSupplierPlants] = useState<SupplierPlant[]>([]);
  const [massConcreteDefaults, setMassConcreteDefaults] = useState<MassConcreteDefault[]>([]);
  const [evaporationDefaults, setEvaporationDefaults] = useState<EvaporationDefault[]>([]);
  const [maturityOptions, setMaturityOptions] = useState<MaturityOption[]>([]);
  const [targetGroups, setTargetGroups] = useState<TargetGroup[]>([]);

  // Plant linking — staged; diffs applied on Save
  const [linkedPlantIds, setLinkedPlantIds] = useState<Set<string>>(new Set());
  const [originalLinkedIds, setOriginalLinkedIds] = useState<Set<string>>(new Set());
  const [linkDates, setLinkDates] = useState<Record<string, string | null>>({});
  // A saved mix design's maturity equation is fixed; using a different
  // equation means creating a new mix design.
  const [equationLocked, setEquationLocked] = useState(false);

  const [formData, setFormData] = useState({
    code: '',
    description: '',
    company_id: user?.CompanyID || '',
    supplier_plant_id: '',
    strength_grade: '',
    rev: '',
    mass_concrete_default_id: '',
    maturity_method_id: '',
    evaporation_default_id: '',
    target_group_id: '',
  });

  // Grade strength is entered in the mix design company's unit
  // (StrengthFormat); no unit is shown until that is known.
  const strengthUnit = useCompanyStrengthUnit(formData.company_id);

  const loadLookups = useCallback(async () => {
    try {
      const [plants, mcDefaults, evapDefaults, nurseSaul, arrhenius] = await Promise.all([
        productsService.listSupplierPlants(undefined, true),
        productsService.listMassConcreteDefaults(),
        productsService.listEvaporationDefaults(),
        maturityService.listNurseSaul().catch(() => [] as NurseSaulTarget[]),
        maturityService.listArrhenius().catch(() => [] as ArrheniusTarget[]),
      ]);
      setSupplierPlants(plants);
      setMassConcreteDefaults(mcDefaults);
      setEvaporationDefaults(evapDefaults);
      setMaturityOptions([
        ...nurseSaul.map((m) => ({
          id: m.id,
          label: `Nurse Saul: ${m.group_description || m.group_id}`,
        })),
        ...arrhenius.map((m) => ({
          id: m.id,
          label: `Arrhenius: ${m.group_description || m.group_id}`,
        })),
      ]);
      // New mix designs start on the company's locked TTF (ASTM C1074)
      // default, datum 0 °C. Existing mixes are never changed here.
      const ttfDefault = isNew ? nurseSaul.find((m) => m.is_default) : undefined;
      if (ttfDefault) {
        setFormData((prev) => (prev.maturity_method_id ? prev : { ...prev, maturity_method_id: ttfDefault.id }));
      }
    } catch (err: any) {
      console.error('Failed to load lookups:', err);
    }
  }, [isNew]);

  const fetchProduct = useCallback(async () => {
    if (!productId || isNew) return;

    try {
      setError(null);

      const [product, plantLinks, groups] = await Promise.all([
        productsService.getProduct(productId),
        productsService.getProductPlants(productId).catch(() => []),
        productsService.listTargetGroups(productId).catch(() => [] as TargetGroup[]),
      ]);
      setFormData({
        code: product.code || '',
        description: product.description || '',
        company_id: product.company_id || '',
        supplier_plant_id: product.supplier_plant_id || '',
        strength_grade: product.strength_grade?.toString() || '',
        rev: product.rev || '',
        mass_concrete_default_id: product.mass_concrete_default_id || '',
        maturity_method_id: product.maturity_method_id || '',
        evaporation_default_id: product.evaporation_default_id || '',
        target_group_id: product.target_group_id || '',
      });
      setEquationLocked(!!product.maturity_method_id);
      setTargetGroups(groups);
      const ids = new Set(plantLinks.map((l) => l.supplier_plant_id));
      setLinkedPlantIds(new Set(ids));
      setOriginalLinkedIds(new Set(ids));
      setLinkDates(Object.fromEntries(plantLinks.map((l) => [l.supplier_plant_id, l.date_linked ?? null])));
    } catch (err: any) {
      setError(apiErrorText(err, t('products.failedToLoad')));
    }
  }, [productId, isNew]);

  // Load lookups and the product together and only then render the form —
  // rendering Selects with a value before their options arrive triggers
  // MUI "out-of-range value" warnings on every render.
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      setLoading(true);
      await Promise.all([loadLookups(), fetchProduct()]);
      if (!cancelled) setLoading(false);
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [loadLookups, fetchProduct]);

  const togglePlant = (plantId: string) => {
    setLinkedPlantIds((prev) => {
      const next = new Set(prev);
      if (next.has(plantId)) {
        next.delete(plantId);
      } else {
        next.add(plantId);
      }
      return next;
    });
  };

  const applyPlantLinkDiffs = async (id: string) => {
    const toLink = Array.from(linkedPlantIds).filter((p) => !originalLinkedIds.has(p));
    const toUnlink = Array.from(originalLinkedIds).filter((p) => !linkedPlantIds.has(p));
    for (const plantId of toLink) {
      await productsService.linkSupplierPlantProduct(plantId, id);
    }
    for (const plantId of toUnlink) {
      await productsService.unlinkSupplierPlantProduct(plantId, id);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);

      // Keep the legacy single-plant column populated (old app + vwPourInfo
      // resolve the plant name from it) without overwriting an existing pick.
      const firstLinked = Array.from(linkedPlantIds)[0];
      const legacyPlantId = formData.supplier_plant_id || firstLinked || undefined;

      if (isNew) {
        const createData: ProductCreate = {
          code: formData.code,
          description: formData.description,
          company_id: formData.company_id,
          supplier_plant_id: legacyPlantId,
          strength_grade: formData.strength_grade ? parseInt(formData.strength_grade) : undefined,
          rev: formData.rev || undefined,
          mass_concrete_default_id: formData.mass_concrete_default_id || undefined,
          maturity_method_id: formData.maturity_method_id || undefined,
          evaporation_default_id: formData.evaporation_default_id || undefined,
          target_group_id: formData.target_group_id || undefined,
        };

        const created = await productsService.createProduct(createData);
        await applyPlantLinkDiffs(created.product_id);
        setSuccess(t('productEdit.createSuccess'));
        setTimeout(() => navigate(`/products/${created.product_id}`), 1000);
      } else {
        const updateData: ProductUpdate = {
          code: formData.code,
          description: formData.description,
          supplier_plant_id: legacyPlantId,
          strength_grade: formData.strength_grade ? parseInt(formData.strength_grade) : undefined,
          rev: formData.rev || undefined,
          // '' clears it: "None" in the form must remove the link, not keep it.
          mass_concrete_default_id: formData.mass_concrete_default_id || '',
          // A saved equation is fixed, so it is only sent while still unset.
          maturity_method_id: equationLocked ? undefined : formData.maturity_method_id || undefined,
          // '' clears it: "None" in the form must remove the link, not keep it.
          evaporation_default_id: formData.evaporation_default_id || '',
          // '' clears it: "None" in the form must remove the link, not keep it.
          target_group_id: formData.target_group_id || '',
        };

        await productsService.updateProduct(productId!, updateData);
        await applyPlantLinkDiffs(productId!);
        setOriginalLinkedIds(new Set(linkedPlantIds));
        if (formData.maturity_method_id) setEquationLocked(true);
        setSuccess(t('productEdit.updateSuccess'));
      }
    } catch (err: any) {
      setError(apiErrorText(err, t('productEdit.failedToSave')));
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: string) => (event: any) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleBack = () => {
    navigate('/products');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Label-on-the-left dropdown row (Figma "Default Targets and Limits")
  const defaultsRow = (
    label: string,
    value: string,
    field: string,
    options: Array<{ id: string; label: string }>,
    disabled = false,
  ) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Typography variant="body2" sx={{ width: 170, flexShrink: 0 }}>
        {label}
      </Typography>
      <FormControl fullWidth size="small" disabled={disabled}>
        <Select value={value} onChange={handleChange(field)} displayEmpty>
          <MenuItem value="">
            <em>{t('common.none', 'None')}</em>
          </MenuItem>
          {options.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ textTransform: 'none' }}
        >
          {t('common.back')}
        </Button>
      </Box>

      <Paper elevation={2} sx={{ p: 3, maxWidth: 760 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={t('productEdit.productCode', 'Mix / Recipe Code')}
              value={formData.code}
              onChange={handleChange('code')}
              required
              fullWidth
              size="small"
              inputProps={{ maxLength: 50 }}
            />

            <TextField
              label={t('productEdit.description', 'Mix / Recipe Description')}
              value={formData.description}
              onChange={handleChange('description')}
              required
              fullWidth
              size="small"
              inputProps={{ maxLength: 50 }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                label={t('productEdit.strengthGrade', 'Grade Strength')}
                value={formData.strength_grade}
                onChange={handleChange('strength_grade')}
                type="number"
                fullWidth
                size="small"
              />
              {strengthUnit && (
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                  {strengthUnit}
                </Typography>
              )}
            </Box>

            <FormControl fullWidth size="small" disabled={equationLocked}>
              <InputLabel>{t('productEdit.maturityEquation', 'Maturity Equation')}</InputLabel>
              <Select
                value={formData.maturity_method_id}
                onChange={handleChange('maturity_method_id')}
                label={t('productEdit.maturityEquation', 'Maturity Equation')}
              >
                <MenuItem value="">{t('common.none', 'None')}</MenuItem>
                {maturityOptions.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
              {equationLocked ? (
                <FormHelperText>
                  {t(
                    'productEdit.maturityEquationLocked',
                    'The equation cannot be changed. Create a new mix design to use a different equation.',
                  )}
                </FormHelperText>
              ) : (
                <FormHelperText>
                  {t(
                    'productEdit.maturityEquationFixedOnSave',
                    'The equation cannot be changed after the mix design is saved.',
                  )}
                </FormHelperText>
              )}
            </FormControl>

            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1.5 }}>
              {t('productEdit.defaultsTitle', 'Default Targets and Limits')}
            </Typography>

            {defaultsRow(
              t('productEdit.massConcreteDefault', 'Temperature'),
              formData.mass_concrete_default_id,
              'mass_concrete_default_id',
              massConcreteDefaults.map((d) => ({ id: d.id, label: d.description })),
            )}
            {defaultsRow(
              t('productEdit.strengthMaturity', 'Strength (Maturity)'),
              formData.target_group_id,
              'target_group_id',
              targetGroups.map((g) => ({
                id: g.target_group_id,
                label: g.target_group_name || g.target_group_id,
              })),
              isNew,
            )}
            {defaultsRow(
              t('productEdit.evaporationDefault', 'Concrete Evaporation'),
              formData.evaporation_default_id,
              'evaporation_default_id',
              evaporationDefaults.map((d) => ({ id: d.id, label: d.description })),
            )}
            {isNew && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5, ml: '186px' }}>
                {t('productEdit.strengthMaturityHint', 'Strength (Maturity) groups are set up after the mix is created.')}
              </Typography>
            )}

            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2 }}>
              {t('productEdit.batchingPlants', 'Batching Plants')}
            </Typography>

            {supplierPlants.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('productEdit.noPlants', 'No batching plants have been created yet.')}
              </Typography>
            ) : (
              <Box
                sx={{
                  overflowX: 'auto',
                  maxHeight: 300,
                  overflowY: 'auto',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary', fontWeight: 400 }}>
                        {t('productEdit.plantName', 'Batch Plant Name')}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontWeight: 400 }}>
                        {t('productEdit.readyMixSupplier', 'Ready Mix Supplier')}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontWeight: 400 }}>
                        {t('productEdit.dateLinked', 'Date Linked')}
                      </TableCell>
                      <TableCell align="center" width={70} sx={{ color: 'text.secondary', fontWeight: 400 }}>
                        {t('productEdit.linked', 'Linked')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {supplierPlants.map((plant) => {
                      const checked = linkedPlantIds.has(plant.supplier_plant_id);
                      return (
                        <TableRow key={plant.supplier_plant_id} hover>
                          <TableCell sx={{ border: 0 }}>{plant.name}</TableCell>
                          <TableCell sx={{ border: 0 }}>{plant.ready_mix_supplier || '—'}</TableCell>
                          <TableCell sx={{ border: 0 }}>
                            {checked && originalLinkedIds.has(plant.supplier_plant_id)
                              ? formatLinkDate(linkDates[plant.supplier_plant_id])
                              : checked
                                ? t('productEdit.linksOnSave', 'on save')
                                : '—'}
                          </TableCell>
                          <TableCell align="center" sx={{ border: 0 }}>
                            <Checkbox
                              size="small"
                              checked={checked}
                              onChange={() => togglePlant(plant.supplier_plant_id)}
                              inputProps={{ 'aria-label': `${t('productEdit.linked', 'Linked')} ${plant.name}` }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                gap: 3,
                justifyContent: 'center',
                alignItems: 'center',
                mt: 2,
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button
                type="submit"
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                disabled={saving || !formData.code.trim() || !formData.description.trim()}
                sx={{ textTransform: 'none', px: 4 }}
              >
                {isNew ? t('productEdit.createProduct') : t('productEdit.updateProduct')}
              </Button>
              <Button
                onClick={handleBack}
                disabled={saving}
                sx={{ textTransform: 'none' }}
              >
                {t('productEdit.cancelReturn', 'Cancel / Return')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ProductEdit;
